/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  Button,
  Confirm,
  Filter,
  ListContextProvider,
  ListToolbar,
  TextField,
  useList,
  useListContext,
  useNotify,
} from "react-admin";

import {
  LinkIcon,
  CloseIcon,
  CancelIcon,
  CreateIcon,
} from "../../../components/common/iconography";

import { productByAccountByActiveByName } from "../../../graphql/queries";
import {
  fetchAllPages,
  isSubscriptionDisabled,
  logError,
} from "../../../utils/appUtils";

import { SearchField } from "../../../components/common/SearchField";
import { CustomField } from "../../../components/common/CustomFields";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { Pagination } from "../../../components/Pagination";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
  Filter: {
    paddingTop: theme.spacing(1),
  },
}));

const ListFilter = ({ account, setProducts, classes, ...props }) => {
  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const newProducts = account.products.items.filter(
      (itm) =>
        itm.name?.toLowerCase().includes(searchTerm) ||
        itm.productCode?.toLowerCase().includes(searchTerm) ||
        itm.category?.toLowerCase().includes(searchTerm) ||
        itm.customFields?.join().toLowerCase().includes(searchTerm),
    );

    setProducts(newProducts);
  };

  return (
    <Filter {...props} variant="outlined" className={classes.Filter}>
      <SearchField
        label="Catalog Search"
        margin="dense"
        alwaysOn
        handleSearch={handleSearch}
        searchOnChange={true}
      />
    </Filter>
  );
};

const PaginationToolbar = ({ ...props }) => {
  const listContext = useListContext(props);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Typography>
          {listContext?.selectedIds?.length > 0 ? (
            `${listContext.selectedIds.length} selected`
          ) : (
            <>&nbsp;</>
          )}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

export const LinkProductDialog = ({
  record: inventory,
  backPath,
  isOpen,
  setIsOpen,
  handleAddSelected,
  ...props
}) => {
  const notify = useNotify();
  const history = useHistory();
  const classes = useStyles();
  const [loaded, setLoaded] = useState(false);
  const [account, setAccount] = useState(inventory.account);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const fixedFilter = { active: "true" };
  const defaultSort = { field: "name", order: "ASC" };
  const defaultPerPage = 10;
  const linkMessage = `Linking will overwrite all custom fields with defaults from Product.
        Linking to the wrong product may affect billing calculations.
        Inventory "${inventory.productName}" will be linked to "${product.name}".`;
  const unlinkMessage = `Removing Product linkage may disable billing calculations for this inventory item.`;
  const nameMessage = `Do you want to reset the Inventory product name from "${inventory.productName}" to "${product.name}"?`;

  useEffect(() => {
    getProducts();
  }, []);

  const getProducts = async () => {
    try {
      debugger;
      // A limit is a page size, not a total: an account with more products
      // than one page would otherwise be missing rows from the picker.
      const items = await fetchAllPages({
        query: productByAccountByActiveByName,
        variables: {
          accountId: inventory.accountId,
          activeName: { beginsWith: { active: "true" } },
        },
        responseKey: "productByAccountByActiveByName",
        pageLimit: 500,
        label: "LinkProductDialog:products",
      });
      setAccount({ ...inventory.account, products: { items } });
      setProducts(items);
      setLoaded(true);
    } catch (error) {
      logError("inventory:LinkProductDialog:getProducts", error, notify);
    }
  };

  const listContext = useList({
    ...props,
    data: products,
    ids: products.map(({ id }) => id),
    loaded: loaded,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/products",
    resource: "products",
  });

  useEffect(() => {
    if (listContext.selectedIds?.length === 2)
      listContext.onSelect([listContext.selectedIds[1]]);
  }, [listContext.selectedIds]);

  useEffect(() => {
    if (isOpen && listContext.selectedIds?.length === 0 && inventory.productId)
      listContext.onSelect([inventory.productId]);
  }, [isOpen]);

  const handleConfirmOpen = () => {
    if (listContext.selectedIds?.length === 1) {
      const product = products.find(
        (itm) => itm.id === listContext.selectedIds[0],
      );
      setProduct(product);
    }
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (
      // do not show rename dialog on unlink product
      product.name?.trim() &&
      inventory.productName?.trim() !== product.name?.trim()
    ) {
      // Skip the name change confirmation; apply the rename directly for now.
      // setRenameOpen(true);
      handleAdd(true);
    } else {
      handleAddSelected(product);
      handleDialogClose();
    }
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return; // ignore backdrop clicks
    }
    setIsOpen(false);
    listContext.onUnselectItems();
    setProducts(account.products.items);
  };

  const handleAdd = (rename) => {
    setRenameOpen(false);
    handleAddSelected(product, rename);
    handleDialogClose();
  };

  const handleCreateProduct = () => {
    history.push({
      pathname: "/products/create",
      state: {
        account: inventory.account,
        backPath: backPath
          ? [...backPath, history.location.pathname]
          : [history.location.pathname],
        _scrollToTop: true,
      },
    });
  };

  return (
    loaded && (
      <Dialog
        maxWidth="xl"
        open={isOpen}
        onClose={handleDialogClose}
        disableEscapeKeyDown={false}
        fullWidth
      >
        <ListContextProvider value={listContext}>
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <ListToolbar
              {...props}
              filters={
                <ListFilter
                  account={account}
                  setProducts={setProducts}
                  classes={classes}
                />
              }
            />
            <IconButton aria-label="close" onClick={handleDialogClose}>
              <CloseIcon fontSize="large" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <CatalogGrid {...props} account={inventory.account} />
            <PaginationToolbar {...props} />
          </DialogContent>
          <DialogActions>
            <Button
              label="Create"
              color="primary"
              style={{ marginRight: "auto" }}
              onClick={handleCreateProduct}
              disabled={isSubscriptionDisabled()}
            >
              <CreateIcon />
            </Button>
            <Button
              label="Cancel"
              color="defalut"
              autoFocus
              onClick={handleDialogClose}
            >
              <CancelIcon />
            </Button>
            <Button
              label="Link Product"
              color="primary"
              onClick={handleConfirmOpen}
            >
              <LinkIcon />
            </Button>
          </DialogActions>
        </ListContextProvider>
        {confirmOpen && (
          <Confirm
            isOpen={confirmOpen}
            title={
              listContext.selectedIds?.length === 0
                ? "Un-Link Confirmation"
                : "Link Confirmation"
            }
            content={
              listContext.selectedIds?.length === 0
                ? unlinkMessage
                : linkMessage
            }
            onConfirm={handleConfirm}
            CancelIcon={CancelIcon}
            onClose={() => setConfirmOpen(false)}
          />
        )}
        {renameOpen && (
          <Confirm
            isOpen={renameOpen}
            title="Name Change Confirmation"
            content={nameMessage}
            confirm="Yes"
            onConfirm={() => handleAdd(true)}
            cancel="No"
            CancelIcon={CancelIcon}
            onClose={() => handleAdd(false)}
          />
        )}
      </Dialog>
    )
  );
};

const CatalogGrid = ({ account, ...props }) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const hasBulkActions = true;
  debugger;
  const customGridFields = account.inventoryCustomFields
    ? account.inventoryCustomFields.map((field, index) => {
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];

  return isSmall ? (
    <CustomizableDatagrid
      rowClick={false}
      basePath="/products"
      hasBulkActions={hasBulkActions}
      onSelect={() => {}}
      storageProp={`LinkProductDialog_${account.id}_small`}
      defaultColumns={["productCode", "name", "defaultUOM"]}
    >
      {[
        <TextField source="productCode" label="Product Code" />,
        <TextField source="name" label="Name" />,
        <TextField source="defaultUOM" label="UOM" />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  ) : (
    <CustomizableDatagrid
      rowClick={false}
      basePath="/products"
      hasBulkActions={hasBulkActions}
      onSelect={() => {}}
      storageProp={`LinkProductDialog_${account.id}_large`}
      defaultColumns={["productCode", "name", "category", "defaultUOM", "pcs"]}
    >
      {[
        <TextField source="productCode" label="Product Code" />,
        <TextField source="name" label="Name" />,
        <TextField source="category" label="Commodity Category" />,
        // <TextField source="totalQuantityOnHand" label="Total Qty." />,
        <TextField source="defaultUOM" label="UOM" />,
        <TextField source="pcs" label="Pieces" />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  );
};
